import React, { useRef, useState } from 'react';
import { ScrollView, View } from 'react-native';

import PropTypes from 'prop-types';

import { VFScreen, VFText } from '@vfgroup-oneplatform/foundation/Components';
import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';

import { UsersCard, ConfirmationModalJourney } from '../../../../Common/UI';

import { QUICK_ACTION_MENU_CONFIG, QUICK_ACTION_MENU_STEP } from './PlanUsers.Enum';
import styles from './PlanUsers.Styles';

function PlanUsers({
  vfScreenProps,
  withTray,
  name,
  priceInfo,
  onRemovePress,
  onAddUsersPress,
  onReturnToBusinessOverviewPress,
  onReturnToPlanPress,
  withRemoveUser,
  users,
  ...props
}) {
  const CMJRemoveRef = useRef(undefined);
  const CMJAddRef = useRef(undefined);
  const [numberOfUsers, setNumberOfUsers] = useState(0);
  const theme = useTheme();

  const _onRemovePress = user => {
    CMJRemoveRef.current.setConfigs({
      confirmationQuickActionConfig:
        QUICK_ACTION_MENU_CONFIG.REMOVE[QUICK_ACTION_MENU_STEP.CONFIRM],
      loadingScreenConfig: QUICK_ACTION_MENU_CONFIG.REMOVE[QUICK_ACTION_MENU_STEP.LOADING],
      successConfigScreen: {
        ...QUICK_ACTION_MENU_CONFIG.REMOVE[QUICK_ACTION_MENU_STEP.SUCCESS],
        descriptionPlaceHolders: [name],
        primaryButtonProps: {
          ...QUICK_ACTION_MENU_CONFIG.REMOVE[QUICK_ACTION_MENU_STEP.SUCCESS].primaryButtonProps,
          onPress: onReturnToPlanPress,
        },
        secondaryButtonProps: {
          ...QUICK_ACTION_MENU_CONFIG.REMOVE[QUICK_ACTION_MENU_STEP.SUCCESS].secondaryButtonProps,
          onPress: onReturnToBusinessOverviewPress,
        },
      },
      failureConfigScreen: QUICK_ACTION_MENU_CONFIG.REMOVE[QUICK_ACTION_MENU_STEP.FAILURE],
      dataToBeSent: {
        user,
      },
    });
    CMJRemoveRef.current.openQAM();
  };
  const _onAddUsersPress = _users => {
    const userConfig =
      _users.length === 1 ? QUICK_ACTION_MENU_CONFIG.ADD_USER : QUICK_ACTION_MENU_CONFIG.ADD_USERS;
    CMJAddRef.current.setConfigs({
      confirmationQuickActionConfig: userConfig[QUICK_ACTION_MENU_STEP.CONFIRM],
      loadingScreenConfig: userConfig[QUICK_ACTION_MENU_STEP.LOADING],
      successConfigScreen: {
        ...userConfig[QUICK_ACTION_MENU_STEP.SUCCESS],
        descriptionPlaceHolders: [name],
        primaryButtonProps: {
          ...userConfig[QUICK_ACTION_MENU_STEP.SUCCESS].primaryButtonProps,
          onPress: onReturnToPlanPress,
        },
      },
      failureConfigScreen: userConfig[QUICK_ACTION_MENU_STEP.FAILURE],
      dataToBeSent: {
        users: _users,
      },
    });
    setNumberOfUsers(_users.length);
    CMJAddRef.current.openQAM();
  };
  const confirmationChildren = () => {
    const { price, currency, isCurrencyFirst } = priceInfo;
    const totalPrice = parseFloat(price * numberOfUsers).toFixed(2);
    return (
      <View style={styles.totalCostContainerStyle}>
        <VFText
          i18nKey="soho_plans_add_users_quick_action_plan_total_cost"
          style={styles.totalCostTitleStyle}
        />
        <View style={styles.divider(theme, 9)} />
        <View style={styles.numberOfUsersContainer}>
          <VFText
            i18nKey="soho_plans_add_users_quick_action_number_of_users"
            style={styles.textStyle('400')}
          />
          <VFText i18nKey={`${numberOfUsers}`} style={styles.textStyle()} />
        </View>
        <View style={styles.planPerUserPerMonthContainer}>
          <VFText
            i18nKey="soho_plans_add_users_quick_action_plan_per_user_per_month"
            style={styles.textStyle('400')}
          />
          <VFText
            i18nKey={isCurrencyFirst ? currency + price : price + currency}
            style={styles.textStyle()}
          />
        </View>
        <View style={styles.totalContainer}>
          <VFText
            i18nKey="soho_plans_add_users_quick_action_plan_total"
            style={styles.textStyle()}
          />
          <VFText
            i18nKey={isCurrencyFirst ? currency + totalPrice : totalPrice + currency}
            style={styles.textStyle()}
          />
        </View>
        <View style={styles.divider(theme, 0, 24)} />
      </View>
    );
  };
  const usersCardProps = () => {
    return {
      withRemoveUser: withRemoveUser,
      ...props,
      onRemovePress: _onRemovePress,
      onAddUsersPress: _onAddUsersPress,
      users: users,
      selectAllButtonTitle: 'soho_plans_add_user_select_all_button_title',
      removeButtonTitle: 'soho_plans_add_user_remove_button_title',
      confirmButtonTitle: 'soho_plans_add_user_confirm_button_title',
      showMoreButtonTitle: 'soho_plans_plan_card_show_more',
      showLessButtonTitle: 'soho_plans_plan_card_show_less',
      assignedUsersTitle: 'soho_plans_add_user_plan_assigned_users_title',
      assignedUsersEmptyText: 'soho_plans_add_user_plan_empty_assigned_users',
      unAssignedUsersTitle: 'soho_plans_add_user_plan_unassigned_users_title',
      unAssignedUsersEmptyText: 'soho_plans_add_user_plan_empty_unassigned_users',
    };
  };

  const getVFScreenProps = () => {
    const { price, currency, isCurrencyFirst } = priceInfo;
    const monthlyPrice = isCurrencyFirst ? `${currency}${price}` : `${price}${currency}`;
    return {
      title: 'soho_plans_add_user_title',
      subHeaderTitle: `${name}`,
      subHeaderDescription: 'soho_plans_add_user_subtitle',
      subHeaderDescriptionPlaceholders: [monthlyPrice],
      subHeaderDescriptionStyle: styles.subHeaderDescriptionStyle,
      subHeaderDescriptionContainerStyle: styles.subHeaderDescriptionContainerStyle,
      withSubHeader: true,
      withHeaderSpace: true,
      ...vfScreenProps,
    };
  };

  return (
    <VFScreen {...getVFScreenProps()}>
      <ScrollView contentContainerStyle={styles.scrollViewContentStyle(withTray)}>
        <VFText
          i18nKey={'soho_plans_add_user_card_title'}
          type="primary"
          style={styles.titleStyle}
        />

        <UsersCard {...usersCardProps()} />

        <ConfirmationModalJourney ref={CMJRemoveRef} functionToBeExecuted={onRemovePress} />
        <ConfirmationModalJourney
          ref={CMJAddRef}
          confirmationChildren={confirmationChildren()}
          functionToBeExecuted={onAddUsersPress}
        />
      </ScrollView>
    </VFScreen>
  );
}

PlanUsers.defaultProps = {
  priceInfo: {},
  withRemoveUser: true,
};

PlanUsers.propTypes = {
  vfScreenProps: PropTypes.object,
  withTray: PropTypes.bool,
  name: PropTypes.string,
  priceInfo: PropTypes.shape({
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    currency: PropTypes.string,
    isCurrencyFirst: PropTypes.bool,
    withPriceVat: PropTypes.bool,
  }),
  onRemovePress: PropTypes.func,
  onAddUsersPress: PropTypes.func,
  onReturnToBusinessOverviewPress: PropTypes.func,
  onReturnToPlanPress: PropTypes.func,
  withRemoveUser: PropTypes.bool,
  users: PropTypes.array,
};

export default PlanUsers;
